/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { View } from '@az/starc-ui';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useFeatureFlag } from '@/features/globalConfig';
import { ShareLinkButton } from '@/features/sharableLink';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import styles from './styles.module.scss';
import cx from 'classnames';
import { usePageType } from '@/hooks/usePageType';
import { pageTypes } from '@/constants/page';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { countryCodes } from '@/constants/locale';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { isBrandShelfPage } from '@/utils/isBrandShelfPage';

const labelMap = {
  ofLabel: 'label_checkout_shipping_Of',
  resultLabel: 'label_Search_Results_Results',
  forLabel: 'label_Search_for',
};

type NumberOfResultsProps = {
  searchText?: string;
  recsPerPage: number;
  totalNumRecs: number;
  removeMargin?: boolean;
};

export const NumberOfResults = ({
  recsPerPage,
  totalNumRecs,
  searchText,
  removeMargin,
}: NumberOfResultsProps) => {
  const router = useLegacyRouter();
  const isSharableLinksEnabled = useFeatureFlag('SHARABLE_LINKS_ENABLED') === 'true';
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const isBrandPage = isBrandShelfPage(router.asPath);
  const { pageType } = usePageType();
  const isShelfOrSearchPage =
    pageType === pageTypes.shelf ||
    pageType === pageTypes.ProductShelf ||
    pageType === pageTypes.search ||
    pageType === pageTypes.MerchShelf;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const isUS = locale === countryCodes.us;
  const useNewShelfDesignMobile =
    useMonetateDecisionFlag('useNewShelfDesignMobile') &&
    isMobile &&
    isUS &&
    (isShelfOrSearchPage || isBrandPage);
  return (
    <>
      {!!recsPerPage && (totalNumRecs || 0) > 0 && (
        <View
          className={cx(styles.numberOfResults, {
            [styles.withMargin]: !removeMargin && !useNewShelfDesignMobile,
            [styles.smallText]: useNewShelfDesignMobile,
          })}
          attributes={{ id: 'search-count-info', 'data-testid': 'search-count-info' }}
        >
          <span>
            <span
              data-testid={isMobile ? 'mobile-results-count' : 'search-count-num'}
              id={isMobile ? 'mobile-results-count' : 'search-count-num'}
              className={styles.countAndTotal}
            >
              <b>
                {totalNumRecs} {labels.resultLabel}
              </b>{' '}
            </span>
            {pageType === pageTypes.search && (
              <>
                {labels.forLabel} <b>&quot;{searchText || router.query.searchText}&quot;</b>
              </>
            )}
          </span>
          {isSharableLinksEnabled && (
            <View className={styles.shareLinkButtonWrapper}>
              <ShareLinkButton
                pageName="shelf"
                classes={{
                  container: useNewShelfDesignMobile || !isMobile ? styles.sharableLink : undefined,
                  button: useNewShelfDesignMobile || !isMobile ? styles.button : undefined,
                }}
              />
            </View>
          )}
        </View>
      )}
    </>
  );
};
